import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { key: 'reviewing',   label: 'Reviewing',       hint: 'I read every idea myself.' },
  { key: 'quoted',      label: 'Quoted',          hint: 'Payment link sent to your inbox.' },
  { key: 'in_progress', label: 'In Production',   hint: 'Materials ordered, press warming up.' },
  { key: 'shipped',     label: 'Shipped',         hint: 'On the truck (or ready for pickup).' },
  { key: 'done',        label: 'Delivered',       hint: 'It\u2019s yours. Enjoy.' },
];

const STATUS_META = {
  new:         { emoji: '📥', tone: 'Idea just landed. Give me a beat.' },
  reviewing:   { emoji: '🔍', tone: 'Reading through everything now.' },
  quoted:      { emoji: '💌', tone: 'Sent you a secure payment link.' },
  in_progress: { emoji: '🖨️', tone: 'Actively making your shirt.' },
  shipped:     { emoji: '📦', tone: 'On the move.' },
  done:        { emoji: '🎉', tone: 'Delivered. Go wear it.' },
  declined:    { emoji: '🙏', tone: 'Not moving forward — check your email.' },
};

const fmt = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
};

const OrderTrackerPage = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/track/${id}`);
        if (!cancelled) setData(r.data);
      } catch (err) {
        if (!cancelled) setError(err?.response?.status === 404
          ? 'Order not found. Check the link in your email — or reach out and I\u2019ll dig it up.'
          : 'Could not load your order right now. Try again in a moment.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const currentIdx = data ? STEPS.findIndex(s => s.key === data.status) : -1;
  const meta = data ? STATUS_META[data.status] || STATUS_META.new : STATUS_META.new;
  const declined = data?.status === 'declined';

  return (
    <main data-testid="tracker-page" className="min-h-screen bg-brand-cream px-6 py-14 md:py-20 relative overflow-hidden">
      <div aria-hidden className="absolute -top-32 -right-32 w-[36rem] h-[36rem] rounded-full opacity-15 blur-3xl pointer-events-none"
        style={{ background: 'radial-gradient(circle, #FF6600 0%, transparent 60%)' }} />

      <div className="relative max-w-3xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 font-display font-black text-xs uppercase tracking-[0.24em] text-black/60 hover:text-black mb-8">
          ← Back to Shirt Happens
        </Link>

        {loading ? (
          <div className="text-black/50 font-body py-24 text-center">Loading your order…</div>
        ) : error ? (
          <div className="rounded-3xl border-2 border-black bg-white p-8 shadow-[8px_8px_0_#FF6600]">
            <div className="font-display font-black text-2xl text-black mb-3">Hmm.</div>
            <p className="font-body text-black/70">{error}</p>
            <Link to="/" className="mt-6 inline-flex font-display font-black text-xs uppercase tracking-[0.24em] px-6 py-3 rounded-full bg-black text-brand-orange hover:bg-brand-orange hover:text-black border-2 border-black transition-colors">
              Home →
            </Link>
          </div>
        ) : data && (
          <>
            <div className="mb-2 font-display font-black text-[11px] tracking-[0.32em] uppercase text-brand-orange">
              Order tracker
            </div>
            <h1 className="font-display font-black text-4xl sm:text-5xl leading-[0.95] text-black tracking-tight mb-2">
              Hey {data.name.split(' ')[0]}, <span className="text-brand-orange">here&rsquo;s where we&rsquo;re at.</span>
            </h1>
            <p className="font-body text-base text-black/70 leading-relaxed mb-8">
              {meta.emoji} {meta.tone}
            </p>

            {/* Progress bar */}
            {!declined && (
              <div data-testid="tracker-progress" className="rounded-3xl border-2 border-black bg-white p-6 md:p-8 shadow-[8px_8px_0_#FF6600] mb-6">
                <div className="grid grid-cols-5 gap-2 sm:gap-3">
                  {STEPS.map((step, i) => {
                    const isDone = i <= currentIdx && currentIdx >= 0;
                    const isCurrent = i === currentIdx;
                    return (
                      <div key={step.key} className="flex flex-col items-center text-center">
                        <div className={[
                          'w-full h-2 rounded-full mb-3 transition-colors',
                          isDone ? 'bg-brand-orange' : 'bg-black/10',
                        ].join(' ')} />
                        <div className={[
                          'w-9 h-9 rounded-full flex items-center justify-center font-display font-black text-xs mb-2 transition-all',
                          isDone ? 'bg-black text-brand-orange border-2 border-black' : 'bg-white text-black/40 border-2 border-black/15',
                          isCurrent ? 'scale-110 ring-4 ring-brand-orange/25' : '',
                        ].join(' ')}>
                          {isDone ? '✓' : i + 1}
                        </div>
                        <div className={[
                          'font-display font-black text-[10px] sm:text-[11px] tracking-[0.14em] uppercase leading-tight',
                          isDone ? 'text-black' : 'text-black/40',
                        ].join(' ')}>{step.label}</div>
                        <div className="hidden sm:block font-body text-[10px] text-black/50 mt-1 leading-tight">{step.hint}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {declined && (
              <div className="rounded-3xl border-2 border-black bg-white p-6 md:p-8 shadow-[8px_8px_0_#FF6600] mb-6">
                <div className="font-display font-black text-xl text-black mb-2">{meta.emoji} Not moving forward</div>
                <p className="font-body text-sm text-black/70">Check your inbox — I sent you the details.</p>
              </div>
            )}

            {/* Payment link CTA if quoted */}
            {data.payment_link_url && data.status === 'quoted' && (
              <a
                href={data.payment_link_url}
                target="_blank" rel="noopener noreferrer"
                data-testid="tracker-pay-link"
                className="block rounded-3xl border-2 border-brand-orange bg-black p-6 md:p-7 shadow-[8px_8px_0_#FF6600] mb-6 group hover:bg-brand-orange transition-colors"
              >
                <div className="font-display font-black text-[10px] tracking-[0.32em] uppercase text-brand-orange group-hover:text-black mb-2">
                  Payment link ready
                </div>
                <div className="font-display font-black text-2xl sm:text-3xl text-white group-hover:text-black mb-2">
                  ${data.payment_link_amount?.toFixed(2)} secured checkout →
                </div>
                <p className="font-body text-sm text-white/70 group-hover:text-black/80">
                  Complete payment to lock in the design and kick off production.
                </p>
              </a>
            )}

            {/* Order summary */}
            <div className="rounded-3xl border-2 border-black/15 bg-white p-6 md:p-8">
              <div className="font-display font-black text-[10px] tracking-[0.32em] uppercase text-black/50 mb-3">
                Your order
              </div>
              <p className="font-body text-sm text-black/80 leading-relaxed mb-5">
                {data.idea_preview || '—'}
              </p>
              {data.items_summary.length > 0 && (
                <div className="rounded-xl overflow-hidden border border-black/10 mb-5">
                  <table className="w-full text-xs sm:text-sm">
                    <thead className="bg-black/5 text-black/60 uppercase tracking-[0.14em] text-[10px]">
                      <tr>
                        <th className="text-left px-3 py-2">Garment</th>
                        <th className="text-left px-3 py-2">Color</th>
                        <th className="text-left px-3 py-2">Size</th>
                        <th className="text-right px-3 py-2">Qty</th>
                      </tr>
                    </thead>
                    <tbody className="text-black/85 divide-y divide-black/5">
                      {data.items_summary.map((li, i) => (
                        <tr key={i}>
                          <td className="px-3 py-2">{li.garment || '—'}</td>
                          <td className="px-3 py-2">{li.color || '—'}</td>
                          <td className="px-3 py-2">{li.size || '—'}</td>
                          <td className="px-3 py-2 text-right font-display font-black text-brand-orange">{li.quantity}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <div className="text-black/40 uppercase tracking-[0.18em] font-display font-black text-[10px] mb-1">Submitted</div>
                  <div className="text-black/80 font-body">{fmt(data.submitted_at)}</div>
                </div>
                <div>
                  <div className="text-black/40 uppercase tracking-[0.18em] font-display font-black text-[10px] mb-1">Last update</div>
                  <div className="text-black/80 font-body">{fmt(data.updated_at || data.submitted_at)}</div>
                </div>
              </div>
            </div>

            <p className="mt-8 font-body text-sm text-black/50 text-center">
              Questions? Just reply to any of my emails — that inbox is me.
            </p>
          </>
        )}
      </div>
    </main>
  );
};

export default OrderTrackerPage;
